import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, MessageSquare, ShoppingBag, Star, User } from 'lucide-react';

export const postPurchaseSurvey: Playbook = {
  meta: {
    id: 'post-purchase-survey',
    slug: 'post-purchase-survey',
    categoryId: 'gather-insights',
    name: 'The Post-Purchase Survey',
    tagline: 'Get feedback right after a sale.',
    description: 'Engage customers immediately after purchase to gather valuable feedback while the buying experience is fresh in their minds.',
    strategy: 'Engage customers immediately after purchase to gather feedback while the experience is fresh.',
    benefits: [
      'Fresh purchase insights',
      'Identify improvement areas',
      'Build customer relationships',
      'Prevent buyer\'s remorse'
    ],
    targetAudience: ['E-commerce stores', 'SaaS companies', 'Service providers'],
    estimatedSetupTime: '18 minutes',
    difficulty: 'beginner',
    tags: ['post-purchase', 'customer-feedback', 'surveys', 'insights'],
    isFeatured: true
  },
  seo: {
    metaTitle: 'Post-Purchase Survey Playbook - Genie Customer Feedback Collection',
    metaDescription: 'Capture valuable customer insights immediately after purchase with conversational Genie surveys. Improve experience and prevent churn.',
    keywords: ['post purchase survey', 'customer feedback', 'purchase experience', 'Genie surveys', 'customer insights']
  },
  demoConfig: {
    triggerType: 'immediate',
    message: "🎉 Thanks for your purchase! Quick question - what convinced you to choose us? Your feedback helps us serve you better.",
    position: 'center'
  },
  demoScript: [
    { speaker: 'agent', message: "Thanks for your purchase! What made you choose us today?" },
    { speaker: 'user', message: "Your customer reviews were really convincing" },
    { speaker: 'agent', message: "That's great to hear! On a scale of 1-10, how was your checkout experience?" },
    { speaker: 'user', message: "9 - very smooth" },
    { speaker: 'agent', message: "Wonderful! One last thing - would you recommend us to a colleague?" }
  ],
  templateConfig: {
    firstMessage: "🎉 Thank you for your purchase! Mind sharing what convinced you to buy?",
    systemPrompt: "You are a feedback-collecting Genie. Gather post-purchase insights in a conversational way. Keep it brief and focus on key insights about the buying decision and experience.",
    tools: ['survey_tools', 'nps_scoring', 'feedback_database']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'Customer completes purchase',
      description: 'User successfully completes transaction and is redirected to confirmation page'
    },
    {
      type: 'genie',
      icon: ShoppingBag,
      genieIcon: 'genie-lamp',
      label: 'Thanks and requests feedback',
      description: 'Genie immediately engages with gratitude and quick feedback request'
    },
    {
      type: 'action',
      icon: MessageSquare,
      genieIcon: 'settings',
      label: 'Asks about purchase decision',
      description: 'Gathers insights about what factors influenced the buying decision'
    },
    {
      type: 'action',
      icon: Bot,
      genieIcon: 'check-circle',
      label: 'Evaluates purchase experience',
      description: 'Asks about checkout process, website experience, and overall satisfaction'
    },
    {
      type: 'action',
      icon: Star,
      genieIcon: 'target',
      label: 'Measures recommendation likelihood',
      description: 'Captures NPS score and identifies potential brand advocates'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'trending-up',
      label: 'Valuable purchase insights',
      description: 'Actionable feedback collected to improve customer experience and marketing'
    }
  ],
  metrics: {
    conversionRate: '43%',
    responseTime: '<1 second',
    satisfactionScore: '4.6/5',
    costSavings: '$20k/month',
    timeToValue: '1 minute'
  },
  testimonials: [
    {
      companyName: 'StyleCraft Boutique',
      authorName: 'Maria Santos',
      authorTitle: 'Customer Experience Manager',
      quote: 'Post-purchase surveys revealed key insights that helped us improve our checkout process. Customer satisfaction increased 23%.',
      metrics: '23% increase in satisfaction'
    }
  ],
  resources: [
    {
      type: 'template',
      title: 'Post-Purchase Question Templates',
      description: 'Proven questions for gathering valuable customer insights after purchase',
      url: '/templates/post-purchase-questions'
    },
    {
      type: 'guide',
      title: 'Customer Feedback Analysis',
      description: 'How to analyze and act on post-purchase feedback data',
      url: '/docs/feedback-analysis'
    }
  ]
};